<template>
  <Page class="max-w-[478px]">
    <div class="mb-8">
      <router-link
        :to="{name: integrationsDashboard}"
        class="text-primary flex items-center font-semibold mb-"
      >
        <TbArrowLeftIcon class="w-4 h-4" /> Back
      </router-link>
    </div>
    <img
      src="../assets/google_tag_manager.svg"
      alt="Google Tag Manager Logo"
      class="h-[48px] self-start mb-4"
    >

    <div
      class="flex flex-col"
    >
      <section>
        <div class="font-semibold text-3xl mb-3">
          Google Tag Manager
        </div>
        <p class="mb-3 sm:max-w-[478px] text-sm">
          Track your customers' checkout process and see how they're progressing.
        </p>

        <form
          @submit.prevent="submit"
        >
          <div class="mb-4">
            <TbInput
              v-model="containerId"
              label="Container ID"
              type="text"
              :placeholder="placeholder"
              :errors="containerIdError && [containerIdError]"
            />
          </div>

          <div>
            <button
              type="submit"
              class="btn btn-primary w-full mb-4"
            >
              {{ buttonLabel }}
            </button>

            <button
              v-if="existingConnection"
              type="button"
              class="btn w-full mb-4"
              @click="disconnect"
            >
              Disconnect
            </button>
          </div>
          <div
            v-if="error"
            class="text-center error text-error-900"
          >
            <small class="error">
              {{ error }}
            </small>
          </div>
        </form>
      </section>
    </div>
  </Page>
</template>

<script setup lang="ts">
import { TbInput, TbArrowLeftIcon } from '@/components/tasty_bistro';
import Page from '@/pages/app/project/dashboard/components/page.vue';

import {
  ref, computed, onActivated, onBeforeMount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  object, string, InferType,
} from 'yup';
import { useForm, useField } from 'vee-validate';
import { getConnection, gtmConnect, gtmDisconnect } from './api';

interface GoogleTagManagerConnection {
  containerId: string;
  createdAt: Date;
}

const existingConnection = ref<GoogleTagManagerConnection | null>(null);

const integrationsDashboard = ref<string>('dashboardIntegrations');

const route = useRoute();
const router = useRouter();
const projectId = route.params.projectId as string;
const error = ref<string>('');

const placeholder = computed(() => existingConnection.value?.containerId || 'Enter Container ID');

const credentialsSchema = object({
  containerId: string()
    .required('You need to provide us with your Container ID so we can connect to your account'),
});

const { handleSubmit, resetForm } = useForm<InferType<typeof credentialsSchema>>({
  validationSchema: credentialsSchema,
});

const { value: containerId, errorMessage: containerIdError } = useField('containerId');

const submit = handleSubmit(async (values) => {
  try {
    const response = await gtmConnect({ projectId, credentials: values });
    existingConnection.value = response;
    router.push({ name: integrationsDashboard.value });
  } catch (err: any) {
    error.value = err.response?.data?.message ?? 'Something went wrong';
  }
});

const buttonLabel = computed(() => (existingConnection.value ? 'Update' : 'Connect'));

const disconnect = async () => {
  try {
    await gtmDisconnect({ projectId });
    existingConnection.value = null;
    router.push({ name: integrationsDashboard.value });
  } catch (err: any) {
    error.value = err.response?.data?.message ?? 'Something went wrong';
  }
};

function resetState() {
  resetForm();
  error.value = '';
}

onActivated(() => {
  resetState();
});

onBeforeMount(async () => {
  const response = await getConnection({ projectId });
  existingConnection.value = response;
});
</script>
